import React from "react";
import styled from "styled-components";

import useStatesByCountry from "hooks/useStatesByCountry";
import SoleraLoader from "components/UI/Loader/SoleraLoader";

import DateDropdown from "./DateDropdown";
import UIDropdown from "components/UI/Dropdown";
import UILabel from "components/UI/Label";
import UITable from "components/UI/Table";
import UIPagination from "components/UI/Pagination";
import UIButtonPrimary from "components/UI/Button/Primary";
import commonStyles from "css/common.module.css";

const Container = styled.div.attrs({ className: "analytics-reporting" })`
  & .row {
    margin: 0 0 24px;
    width: 100%;
  }
  & .control {
    display: inline-block;
    margin: 0 24px 0 0;
    width: calc(25% - 24px);
  }
`;

const formatDate = x =>
  new Date(x).toLocaleDateString("en-us", {
    year: "numeric",
    month: "numeric",
    day: "numeric"
  });

const getRow = (data) => (
  <UITable.Row key={data.workItemId}>
    <UITable.Cell>{formatDate(data.creationDate)}</UITable.Cell>
    <UITable.Cell>{data.cInsurer}</UITable.Cell>
    <UITable.Cell>{data.claimNumber}</UITable.Cell>
    <UITable.Cell>{data.status}</UITable.Cell>
    <UITable.Cell>{data.state}</UITable.Cell>
    <UITable.Cell>{data.prorated ? "Yes" : "No"}</UITable.Cell>
    <UITable.Cell>{data.status === "IN PROGRESS" ? "" : formatDate(data.completedDate)}</UITable.Cell>
    <UITable.Cell>{data.isClone === "True" ? "Yes" : "No"}</UITable.Cell>
  </UITable.Row>
);

const mapStateToDropdownItem = ({ code, name }) => ({
  value: code,
  text: name
});

const BasicUsageReport = (props) => {
  const [stateList, statesLoading] = useStatesByCountry("US");
  const stateItems = stateList === null ? [] : stateList.map(mapStateToDropdownItem);
  if (Object.keys(props.records).length === 0){
    props.data.selectedStates = [];
    props.data.start_date = formatDate(new Date());
    props.data.end_date = formatDate(new Date());
    props.data.records = 25;
    props.data.page = 1;
    props.data.totalPages = 0;
  }
  else{
    props.data.selectedStates = props.records.filters.selectedStates;
    props.data.start_date = formatDate(props.records.filters.start_date);
    props.data.end_date = formatDate(props.records.filters.end_date);
    props.data.records = props.records.filters.records;
    props.data.page = props.records.filters.page;
    props.data.totalPages = Math.ceil(props.records.count / props.data.records);
  }
  const onStateSelected = (e) => {
    props.data.selectedStates = (!props.data.selectedStates.map(a => a.value).includes(e.value)) ? [...props.data.selectedStates, {value: e.value, text:e.text}] : props.data.selectedStates.filter(item => item.value !== e.value);
    props.data.page = 1;
    props.data.type = "data";
    props.basicUsageReport(props.data);
  }

  const onDateSelected = (e) => {
    props.data.start_date = formatDate(e.start_date);
    props.data.end_date = formatDate(e.end_date);
    props.data.page = 1;
    props.data.type = "data";
    props.basicUsageReport(props.data);
  }

  const onChangePage = (e) => {
    props.data.page = e;
    props.data.type = "data";
    props.basicUsageReport(props.data);
  }

  const onArrowChangePage = (e) => {
    const option = e._dispatchInstances.memoizedProps.title;
    switch(option){
      case "Next page":
        props.data.page = props.data.page + 1
        break;
      case "Previous page":
        props.data.page = props.data.page - 1
        break;
      case "Go to First Page":
        props.data.page = 1
        break;
      case "Go to Last Page":
        props.data.page = props.data.totalPages
        break;
    }
    props.data.type = "data";
    props.basicUsageReport(props.data);
  }

  const onChangeDisplaying = (e) => {
    props.data.records = e;
    props.data.page = 1;
    props.data.type = "data";
    props.basicUsageReport(props.data);
  }

  const downloadBasicUsage = () =>{
    props.data.type = "report";
    props.data.exportPage = 1;
    props.data.exportRecords = props.records.count;
    props.basicUsageReport(props.data);
  }
  
  const getTable = (records) => (
    <div>
      <div className="row">
        <UIButtonPrimary
          type="button"
          onClick={downloadBasicUsage}
        >
          Download Report
        </UIButtonPrimary>
      </div>
      <UITable>
        <UITable.Header>
          <UITable.Row>
            <UITable.HeaderCell>Entry Date</UITable.HeaderCell>
            <UITable.HeaderCell>Insurer Name</UITable.HeaderCell>
            <UITable.HeaderCell>Claim #</UITable.HeaderCell>
            <UITable.HeaderCell>Status</UITable.HeaderCell>
            <UITable.HeaderCell>State</UITable.HeaderCell>
            <UITable.HeaderCell>Prorated</UITable.HeaderCell>
            <UITable.HeaderCell>Completed Date</UITable.HeaderCell>
            <UITable.HeaderCell>Is Clone</UITable.HeaderCell>
          </UITable.Row>
        </UITable.Header>
  
        <UITable.Body>
          {records}
        </UITable.Body>
      </UITable>
  
      <UITable.Footer>
        <UIPagination 
          endPage={props.data.totalPages}
          activePage={props.data.page}
          displaying={props.data.records}
          displayOptions={[10,25,50]}
          hasPrevPage={(props.data.page > 1)}
          hasNextPage={(props.data.page < props.data.totalPages)}
          onChangeDisplaying={onChangeDisplaying}
          onChangePage={onChangePage}
          onNextPage={onArrowChangePage}
          onPrevPage={onArrowChangePage}
          onFirstPage={onArrowChangePage}
          onLastPage={onArrowChangePage}
        />
      </UITable.Footer>
    </div>
  )

  const records = Object.keys(props.records).length === 0 ? null : props.records.basicUsageRecords.map(a => getRow(a));
  const table = props.records.count ? getTable(records) : null;

  return (
    <Container
      className={`${commonStyles.page} ${commonStyles.padded} ${
        commonStyles.clear
      }`}
    >
      <SoleraLoader
        open={statesLoading}
      />

      <div className="row">
        <div className="control">
          <UILabel>State:</UILabel>
          <UIDropdown multiple options={stateItems} value={props.data.selectedStates.map(a => a.text).join(", ")} selectedItems={props.data.selectedStates} onChange={onStateSelected} />
        </div>
        <div className="control">
          <UILabel>Dates:</UILabel>
          <DateDropdown onChange={onDateSelected} />
        </div>
      </div>
      {table}
    </Container>
  );
};

export default BasicUsageReport;
